import { Button, Dropdown, FormLayout, TextInput } from 'components/common'
import {
  ftpProtocolOptions,
  FTP_PROTOCOLS,
} from 'components/aggregatorManagement/utils'
import { useForm } from 'react-hook-form'
import SwitchField from 'components/bundleProduct/switchField'
import {
  ALERT_NUMERIC_FIELD_KEYS,
  buildLogConfig,
  configRootFields,
  configurationsFields,
} from './utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import ManualCrons from './manualCrons'
import { PAGE_CRON_JOBS } from 'utils/constants'
import toast from 'react-hot-toast'
import { createConfigurations, updateConfigurations } from 'utils/api/configApi'
import { AxiosError } from 'axios'
import {
  BagConfig,
  Configuration,
  ConfigurationWithId,
} from 'types/configuration'
import ErpSection from './erpSection'
import { getNestedErrorMessage } from 'utils/helpers/getNestedErrorMessage'
import RmaSection from './rmaSection'
import ClusterSection from './clusterSection'
import { configurationSchema } from 'utils/schema/configurationsSchema'
import SapConfigurationSection from './sapConfigurationSection'
import SpecialCategoriesSection from './specialCategoriesSection'
import DarkStoresSection from './darkStoresSection'
import LogConfigSection from './logConfigSection'

const {
  SQL_USER_ERP_DB,
  SQL_HOST,
  SQL_PORT,
  SQL_USERNAME,
  SQL_PASSWORD,
  SQL_DATABASE_NAME,

  SMTP_HOST,
  SMTP_USERNAME,
  SMTP_PASSWORD,
  SMTP_FROM_EMAIL,
  SMTP_FROM_NAME,

  EXPRESSION_FRESH_MANDATORY_BARCODE_RESET,
  INVOICE_ADDRESS,
  INVOICE_PHONE,
  INVOICE_REG_NO,

  PURCHASE_TIMER_BUFFER,
  SPECIAL_PRICE_BUFFER,
  LASTMILE_MAX_ORDER_AGE_HOURS,

  ADMIN_EMAIL,
  CS_NUMBER,
  MAX_UPLOAD_SIZE,
  LOG_DELETION_PERIOD,
  MAX_CONCURRENT_AGGREGATORS,
  FRESH_BARCODE_PREFIX,
  MAX_ALLOWED_PRICE_DIFF_PERCENT,
  MAX_ALLOWED_TOTAL_PRICE_DIFF_PERCENT,
  MAX_ALLOWED_PRICE_DIFF: MAX_ALLOW_PRICE_DIFF,

  LAST_MILE_ORDER_CANCEL,
  LAST_MILE_ORDER_CREATE,
  LAST_MILE_ORDER_UPDATE,
  LAST_MILE_ORDER_UPDATE_CREATES,
  LAST_MILE_TOKEN,

  // SMS_MOBILE_NUMBERS,
  SMS_PASSWORD,
  SMS_SENDER_ID,
  SMS_URL,
  SMS_USERNAME,
  SMS_MOBILE_NUMBERS,

  THIRD_PARTY_FTP_PROTOCOL,
  THIRD_PARTY_FTP_HOST,
  THIRD_PARTY_FTP_PORT,
  THIRD_PARTY_FTP_USERNAME,
  THIRD_PARTY_FTP_PASSWORD,
  SMS_THROTTLE_MINUTES,
  SYNC_FAILURE_SMS_THROTTLE_MINUTES,

  PHARMACY_ENABLE_3PL_IMPORT,
} = configurationsFields

type Props = {
  configuration?: ConfigurationWithId
  isConfigured: boolean
  completeConfiguration: () => void
}

const ConfigurationForm = ({
  configuration,
  isConfigured,
  completeConfiguration,
}: Props) => {
  const transformBagsToFormData = (bags?: BagConfig[]) => {
    const bagPrices: Record<string, number> = {}
    const bagBarcodes: Record<string, string> = {}

    bags?.forEach((bag) => {
      // Map each bag to the form structure
      bagPrices[bag.size] = bag.price || 0
      bagBarcodes[bag.size] = bag.barcode || ''
    })

    return { bagPrices, bagBarcodes }
  }

  // Initialize form with backend data
  const initialBagData = transformBagsToFormData(configuration?.mobile?.bags)

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm<any>({
    resolver: yupResolver(configurationSchema),
    defaultValues: {
      ...configuration,
      [FRESH_BARCODE_PREFIX.key]:
        configuration?.[FRESH_BARCODE_PREFIX.key]?.join(',').trim() ?? '',
      // Purchase Timer buffer — fall back to 0 for configs created before
      // this field existed.
      [PURCHASE_TIMER_BUFFER.key]:
        configuration?.purchaseTimerBufferMinutes ?? 0,
      // Special Price buffer — same fallback for pre-existing configs.
      [SPECIAL_PRICE_BUFFER.key]: configuration?.specialPriceBufferMinutes ?? 0,
      // Lastmile order-age window — same fallback, at the backend's default.
      [LASTMILE_MAX_ORDER_AGE_HOURS.key]:
        configuration?.lastMileMaxOrderAgeHours ?? 6,
      // Every unknown/missing module defaults to enabled (true).
      logConfig: buildLogConfig(configuration?.logConfig),
      ...initialBagData,
    },
  })
  const [loading, setLoading] = useState(false)

  const handleChangeConfigurations = async (data: Configuration) => {
    const configToast = toast.loading(
      `${isConfigured ? 'Updating' : 'Creating'} Configurations...`,
    )
    const formData = data as Configuration & {
      bagBarcodes?: Record<string, string>
      bagPrices?: Record<string, number>
    }

    // Transform bag data if present
    const transformedData = { ...formData }
    if (formData.bagBarcodes || formData.bagPrices) {
      // Build bags array from the form data
      const bagSizes = new Set(
        configuration?.mobile?.bags?.map((bag) => bag.size) || [],
      )

      // Filter out any undefined/null entries and only keep existing bag sizes
      const validBagSizes = Array.from(bagSizes).filter((size) => {
        const hasBarcode = formData.bagBarcodes?.[size] !== undefined
        const hasPrice = formData.bagPrices?.[size] !== undefined
        return hasBarcode || hasPrice
      })

      if (validBagSizes.length > 0) {
        transformedData.mobile.bags = validBagSizes.map((size) => {
          const originalBag = configuration?.mobile?.bags?.find(
            (bag) => bag.size === size,
          )

          return {
            size,
            barcode: formData.bagBarcodes?.[size] ?? originalBag?.barcode ?? '',
            price: formData.bagPrices?.[size] ?? originalBag?.price ?? 0,
            ...(originalBag?.img && { img: originalBag.img }),
          }
        })
      } else {
        // Keep original bags if no valid form data
        transformedData.mobile.bags = configuration?.mobile?.bags || []
      }

      // Clean up
      delete (transformedData as any).bagBarcodes
      delete (transformedData as any).bagPrices
    }

    // Alert settings are saved from the Alert Config page, not here
    const restData: Record<string, unknown> = { ...transformedData }
    ;[
      configRootFields.ALERT,
      configRootFields.NOTIFICATION_CHANNELS,
      ...ALERT_NUMERIC_FIELD_KEYS,
      // Cron expressions are saved from the Cron Jobs page
      configRootFields.EXPRESSION,
    ].forEach((key) => delete restData[key])

    const config = {
      ...(restData as unknown as Configuration),
      freshBarcodePrefix: transformedData?.freshBarcodePrefix
        ? (transformedData?.freshBarcodePrefix as unknown as string).split(',')
        : [],
      // Always send a clean integer (empty input → 0) so the buffer can be
      // reset to 0 and the backend's isInt validation never sees an empty
      // string.
      purchaseTimerBufferMinutes:
        Number(transformedData?.purchaseTimerBufferMinutes) || 0,
      // Same clean-integer treatment as the purchase-timer buffer.
      specialPriceBufferMinutes:
        Number(transformedData?.specialPriceBufferMinutes) || 0,
      // Cleared box falls back to 6 rather than being sent as '' (a 422) — the
      // same value the cron itself defaults to when the field is unset.
      lastMileMaxOrderAgeHours:
        Number(transformedData?.lastMileMaxOrderAgeHours) || 6,
    }
    setLoading(true)

    try {
      // Rest of your code remains exactly the same...
      if (isConfigured) {
        await updateConfigurations(configuration!._id, config)
        toast.success('Configuration updated successfully.', {
          id: configToast,
        })
      } else {
        await createConfigurations(config)
        completeConfiguration()
        toast.success('Configuration created successfully.', {
          id: configToast,
        })
      }
    } catch (error) {
      // Error handling remains the same...
      if (error instanceof AxiosError) {
        const MESSAGE = error?.response?.data?.errors?.msg

        // logConfig is validated strictly (unknown key / non-boolean value) and
        // its message names the offending modules, so surface it as-is.
        if (MESSAGE?.[0]?.param === 'logConfig' && MESSAGE?.[0]?.msg) {
          return toast.error(MESSAGE[0].msg, { id: configToast })
        }

        toast.error(
          `Failed to ${isConfigured ? 'Update' : 'Create'} Configurations.`,
          { id: configToast },
        )
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-6xl">
      {!isConfigured && (
        <h1 className="title text-3xl">Create Configuration</h1>
      )}

      <form onSubmit={handleSubmit(handleChangeConfigurations)}>
        {/* <ErpSection
          title="Master ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_MASTER}
        />
        <ErpSection
          title="Inventory ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_INVENTORY}
        />
        <ErpSection
          title="Pricing ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_PRICING}
        />
        <ErpSection
          title="Block Sale Status ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.BLOCK_SALE_POS}
        />
        <ErpSection
          title="Order Create ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_ORDER_CREATE}
        />
        <ErpSection
          title="Reconciliation ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_RECONCILIATION}
        /> */}
        <ErpSection
          title="Fresh System Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.FRESH}
        />
        {/* Last Mile */}
        <h3 className="title mt-3">Last Mile Loginext Configurations</h3>
        <FormLayout>
          <div>
            <TextInput
              required
              register={register}
              name={
                LAST_MILE_ORDER_CREATE.parentKey +
                '.' +
                LAST_MILE_ORDER_CREATE.key
              }
              label={LAST_MILE_ORDER_CREATE.label}
              error={getNestedErrorMessage(errors, LAST_MILE_ORDER_CREATE)}
            />
            <TextInput
              required
              register={register}
              name={
                LAST_MILE_ORDER_UPDATE.parentKey +
                '.' +
                LAST_MILE_ORDER_UPDATE.key
              }
              label={LAST_MILE_ORDER_UPDATE.label}
              error={getNestedErrorMessage(errors, LAST_MILE_ORDER_UPDATE)}
            />
            <TextInput
              required
              register={register}
              name={
                LAST_MILE_ORDER_CANCEL.parentKey +
                '.' +
                LAST_MILE_ORDER_CANCEL.key
              }
              label={LAST_MILE_ORDER_CANCEL.label}
              error={getNestedErrorMessage(errors, LAST_MILE_ORDER_CANCEL)}
            />
            <TextInput
              required
              register={register}
              name={
                LAST_MILE_ORDER_UPDATE_CREATES.parentKey +
                '.' +
                LAST_MILE_ORDER_UPDATE_CREATES.key
              }
              label={LAST_MILE_ORDER_UPDATE_CREATES.label}
              error={getNestedErrorMessage(
                errors,
                LAST_MILE_ORDER_UPDATE_CREATES,
              )}
            />
            <TextInput
              required
              register={register}
              name={LAST_MILE_TOKEN.parentKey + '.' + LAST_MILE_TOKEN.key}
              label={LAST_MILE_TOKEN.label}
              error={getNestedErrorMessage(errors, LAST_MILE_TOKEN)}
            />
          </div>
        </FormLayout>

        <SapConfigurationSection register={register} errors={errors} />
        {/* SQL */}
        <h3 className="title mt-3">SQL Configurations</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <Dropdown
              setValue={setValue}
              defaultValue={configuration?.sql?.useErpDb ?? false}
              options={[
                { label: 'Database', value: true },
                { label: 'API', value: false },
              ]}
              name={SQL_USER_ERP_DB.parentKey + '.' + SQL_USER_ERP_DB.key}
              label={SQL_USER_ERP_DB.label}
              error={errors?.[SQL_USER_ERP_DB.key]?.message as string}
              hintText="This selection will affect both inventory and pricing crons."
            />
            {/* SQL Host */}
            <TextInput
              required
              register={register}
              name={SQL_HOST.parentKey + '.' + SQL_HOST.key}
              label={SQL_HOST.label}
              error={getNestedErrorMessage(errors, SQL_HOST)}
            />

            {/* SQL Username */}
            <TextInput
              required
              register={register}
              name={SQL_USERNAME.parentKey + '.' + SQL_USERNAME.key}
              label={SQL_USERNAME.label}
              error={getNestedErrorMessage(errors, SQL_USERNAME)}
            />

            {/* SQL Password */}
            <TextInput
              required
              type="password"
              register={register}
              name={SQL_PASSWORD.parentKey + '.' + SQL_PASSWORD.key}
              label={SQL_PASSWORD.label}
              error={getNestedErrorMessage(errors, SQL_PASSWORD)}
            />

            {/* SQL Port */}
            <TextInput
              required
              register={register}
              name={SQL_PORT.parentKey + '.' + SQL_PORT.key}
              label={SQL_PORT.label}
              error={getNestedErrorMessage(errors, SQL_PORT)}
            />

            {/* SQL Database Name */}
            <TextInput
              required
              register={register}
              name={SQL_DATABASE_NAME.parentKey + '.' + SQL_DATABASE_NAME.key}
              label={SQL_DATABASE_NAME.label}
              error={getNestedErrorMessage(errors, SQL_DATABASE_NAME)}
            />
          </div>
        </FormLayout>
        {/* SMTP */}
        <h3 className="title mt-3">SMTP Configurations</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            {/* SMTP Host */}
            <TextInput
              required
              register={register}
              name={SMTP_HOST.parentKey + '.' + SMTP_HOST.key}
              label={SMTP_HOST.label}
              error={getNestedErrorMessage(errors, SMTP_HOST)}
            />

            {/* SMTP Username */}
            <TextInput
              required
              register={register}
              name={SMTP_USERNAME.parentKey + '.' + SMTP_USERNAME.key}
              label={SMTP_USERNAME.label}
              error={getNestedErrorMessage(errors, SMTP_USERNAME)}
            />

            {/* SMTP Password */}
            <TextInput
              required
              type="password"
              register={register}
              name={SMTP_PASSWORD.parentKey + '.' + SMTP_PASSWORD.key}
              label={SMTP_PASSWORD.label}
              error={getNestedErrorMessage(errors, SMTP_PASSWORD)}
            />

            {/* SMTP From Name */}
            <TextInput
              required
              register={register}
              name={SMTP_FROM_NAME.parentKey + '.' + SMTP_FROM_NAME.key}
              label={SMTP_FROM_NAME.label}
              error={getNestedErrorMessage(errors, SMTP_FROM_NAME)}
              hintText="Enter the name you want to appear as the sender in emails"
            />

            {/* SMTP From Email */}
            <TextInput
              required
              register={register}
              name={SMTP_FROM_EMAIL.parentKey + '.' + SMTP_FROM_EMAIL.key}
              label={SMTP_FROM_EMAIL.label}
              error={getNestedErrorMessage(errors, SMTP_FROM_EMAIL)}
              hintText="Enter the email address you want to use as the sender for emails"
            />
          </div>
        </FormLayout>
        {/* Invoice Config */}
        <h3 className="title mt-3">Invoice Configurations</h3>
        <FormLayout>
          <div className="flex flex-col gap-3">
            {/* Vat Reg */}
            <TextInput
              required
              register={register}
              name={INVOICE_REG_NO.parentKey + '.' + INVOICE_REG_NO.key}
              label={INVOICE_REG_NO.label}
              error={getNestedErrorMessage(errors, INVOICE_REG_NO)}
            />

            <TextInput
              required
              register={register}
              name={INVOICE_PHONE.parentKey + '.' + INVOICE_PHONE.key}
              label={INVOICE_PHONE.label}
              error={getNestedErrorMessage(errors, INVOICE_PHONE)}
            />

            <TextInput
              required
              register={register}
              name={INVOICE_ADDRESS.parentKey + '.' + INVOICE_ADDRESS.key}
              label={INVOICE_ADDRESS.label}
              error={getNestedErrorMessage(errors, INVOICE_ADDRESS)}
            />
          </div>
        </FormLayout>
        {/* cron */}
        <h3 className="title mt-3">Cron Configurations</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <p className="text-sm text-gray-500 mid-md:col-span-3">
              Cron schedules are changed, and crons switched on or off, on the{' '}
              <Link
                to={PAGE_CRON_JOBS.path}
                className="font-semibold underline"
              >
                Cron Jobs
              </Link>{' '}
              page.
            </p>

            {/* Fresh Mandatory Barcode Reset */}
            <TextInput
              required
              register={register}
              name={
                EXPRESSION_FRESH_MANDATORY_BARCODE_RESET.parentKey +
                '.' +
                EXPRESSION_FRESH_MANDATORY_BARCODE_RESET.key
              }
              label={EXPRESSION_FRESH_MANDATORY_BARCODE_RESET.label}
              error={getNestedErrorMessage(
                errors,
                EXPRESSION_FRESH_MANDATORY_BARCODE_RESET,
              )}
              placeholder="e.g., 0 2 * *"
              hintText="Nightly job that sets every Fresh mandatory barcode back to 1000"
            />

            <div className="mid-md:col-span-3">
              <ManualCrons />
            </div>
          </div>
        </FormLayout>

        {/* Purchase Timer Config */}
        <h3 className="title mt-3">Purchase Timer Configuration</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <TextInput
              type="number"
              min={0}
              register={register}
              name={PURCHASE_TIMER_BUFFER.key}
              label={PURCHASE_TIMER_BUFFER.label}
              error={
                (errors as Record<string, any>)?.[PURCHASE_TIMER_BUFFER.key]
                  ?.message
              }
              hintText="Extra minutes added before the start and after the end of every product's purchase-timer window. Set 0 to disable the buffer."
            />
            <TextInput
              type="number"
              min={0}
              register={register}
              name={SPECIAL_PRICE_BUFFER.key}
              label={SPECIAL_PRICE_BUFFER.label}
              error={
                (errors as Record<string, any>)?.[SPECIAL_PRICE_BUFFER.key]
                  ?.message
              }
              hintText="Extra minutes added before the start and after the end of every product's special-price time window. Set 0 to disable the buffer."
            />
          </div>
        </FormLayout>
        {/* RMA Configuration */}
        <RmaSection
          setValue={setValue}
          condition={configuration?.rmaConditions}
          reason={configuration?.rmaReasons}
          conditionCodeMap={configuration?.rmaReasonCodeMap ?? {}}
          errors={errors}
          watch={watch}
        />
        <SpecialCategoriesSection
          setValue={setValue}
          specialCategories={configuration?.specialCategories}
          errors={errors}
          loading={loading}
        />
        {/* Clusters */}
        <ClusterSection
          setValue={setValue}
          clusters={configuration?.clusters}
          errors={errors}
        />
        {/* Dark Stores */}
        <DarkStoresSection
          setValue={setValue}
          darkStores={configuration?.darkStores}
          errors={errors}
        />
        {/* Other */}
        <h3 className="title mt-3">Other</h3>
        <FormLayout>
          <>
            <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
              {/* Max Upload Size */}
              <TextInput
                required
                register={register}
                name={MAX_UPLOAD_SIZE.key}
                label={MAX_UPLOAD_SIZE.label}
                error={
                  (errors as Record<string, any>)?.[MAX_UPLOAD_SIZE.key]
                    ?.message
                }
                hintText="Specify the maximum file size allowed for uploads. Enter the size in megabytes (MB)."
              />

              {/* Log Deletion Period */}
              <TextInput
                required
                register={register}
                name={LOG_DELETION_PERIOD.key}
                label={LOG_DELETION_PERIOD.label}
                error={
                  (errors as Record<string, any>)?.[LOG_DELETION_PERIOD.key]
                    ?.message
                }
                hintText="Enter the number of days to retain logs. Logs older than this duration will be automatically deleted."
              />

              {/* Admin Email */}
              <TextInput
                required
                register={register}
                name={ADMIN_EMAIL.key}
                label={ADMIN_EMAIL.label}
                error={
                  (errors as Record<string, any>)?.[ADMIN_EMAIL.key]?.message
                }
                hintText="Provide an email to receive error notifications. Separate multiple emails with commas (',')"
              />

              {/* CS Number */}
              <TextInput
                required
                register={register}
                name={CS_NUMBER.key}
                label={CS_NUMBER.label}
                error={
                  (errors as Record<string, any>)?.[CS_NUMBER.key]?.message
                }
                hintText="Customer Support phone number. Used as a fallback when an order has no valid contact number."
              />

              {/* MAX_CONCURRENT_AGGREGATORS */}
              <TextInput
                required
                register={register}
                name={MAX_CONCURRENT_AGGREGATORS.key}
                label={MAX_CONCURRENT_AGGREGATORS.label}
                error={
                  (errors as Record<string, any>)?.[
                    MAX_CONCURRENT_AGGREGATORS.key
                  ]?.message
                }
                hintText="Specify maximum number of concurrent aggregator processes"
              />

              {/* Fresh Item Prefix */}
              <TextInput
                register={register}
                name={FRESH_BARCODE_PREFIX.key}
                label={FRESH_BARCODE_PREFIX.label}
                error={
                  (errors as Record<string, any>)?.[FRESH_BARCODE_PREFIX.key]
                    ?.message
                }
                hintText="Enter one or more prefixes to identify fresh items. Separate multiple prefixes with commas (',')"
              />
            </div>

            <hr className="my-6" />

            <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
              {/* Max Allow Price Diff */}
              <TextInput
                register={register}
                name={MAX_ALLOW_PRICE_DIFF.key}
                label={MAX_ALLOW_PRICE_DIFF.label}
                error={
                  (errors as Record<string, any>)?.[MAX_ALLOW_PRICE_DIFF.key]
                    ?.message
                }
                hintText="Maximum allowed price difference (in amount) between two product prices. If the difference exceeds this value, the product will be flagged and the order will be placed on hold."
              />

              {/* Max Allowed Price Difference */}
              <TextInput
                register={register}
                name={MAX_ALLOWED_PRICE_DIFF_PERCENT.key}
                label={MAX_ALLOWED_PRICE_DIFF_PERCENT.label}
                error={
                  (errors as Record<string, any>)?.[
                    MAX_ALLOWED_PRICE_DIFF_PERCENT.key
                  ]?.message
                }
                hintText="Maximum allowed price difference (in %) between the aggregator order price and ERP price. If the percentage difference exceeds this value, the order will be placed on hold."
              />

              {/* Max Allowed Total Price Difference */}
              <TextInput
                register={register}
                name={MAX_ALLOWED_TOTAL_PRICE_DIFF_PERCENT.key}
                label={MAX_ALLOWED_TOTAL_PRICE_DIFF_PERCENT.label}
                error={
                  (errors as Record<string, any>)?.[
                    MAX_ALLOWED_TOTAL_PRICE_DIFF_PERCENT.key
                  ]?.message
                }
                hintText="Maximum allowed total price difference (in %) between the aggregator order total and calculated total amount. If the percentage difference exceeds this value, the order will be placed on hold."
              />
            </div>

            {/*
              Root-level field, so it is registered by bare key rather than
              under `parentKey` like the boxes above. It lives in this card
              because that is where a lastmile setting is looked for, but the
              window it sets covers Loginext too - hence the hint.
            */}
            <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
              <TextInput
                type="number"
                min={1}
                name={LASTMILE_MAX_ORDER_AGE_HOURS.key}
                label={LASTMILE_MAX_ORDER_AGE_HOURS.label}
                error={
                  (errors as Record<string, any>)?.[
                    LASTMILE_MAX_ORDER_AGE_HOURS.key
                  ]?.message
                }
                register={register}
                hintText="Hours an order may be old and still be retried by the lastmile sync cron. Anything older is skipped and never sent. Applies to Loginext and Instashop alike. Default 6."
              />
            </div>
          </>
        </FormLayout>

        {/* Log Configuration */}
        <LogConfigSection
          setValue={setValue}
          logConfig={configuration?.logConfig}
        />
        {/* SMS configuration */}
        <h3 className="title mt-3">SMS Configurations</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <TextInput
              required
              register={register}
              name={SMS_URL.parentKey + '.' + SMS_URL.key}
              label={SMS_URL.label}
              error={getNestedErrorMessage(errors, SMS_URL)}
            />
            <TextInput
              required
              register={register}
              name={SMS_USERNAME.parentKey + '.' + SMS_USERNAME.key}
              label={SMS_USERNAME.label}
              error={getNestedErrorMessage(errors, SMS_USERNAME)}
            />
            <TextInput
              required
              register={register}
              type="password"
              name={SMS_PASSWORD.parentKey + '.' + SMS_PASSWORD.key}
              label={SMS_PASSWORD.label}
              error={getNestedErrorMessage(errors, SMS_PASSWORD)}
            />
            <TextInput
              required
              register={register}
              name={SMS_SENDER_ID.parentKey + '.' + SMS_SENDER_ID.key}
              label={SMS_SENDER_ID.label}
              error={getNestedErrorMessage(errors, SMS_SENDER_ID)}
            />
            <TextInput
              required
              register={register}
              name={SMS_SENDER_ID.parentKey + '.' + SMS_MOBILE_NUMBERS.key}
              label={SMS_MOBILE_NUMBERS.label}
              error={getNestedErrorMessage(errors, SMS_MOBILE_NUMBERS)}
              hintText="Provide mobile numbers to receive error notifications. Separate multiple mobile numbers with commas (',')"
            />
            <TextInput
              type="number"
              min={1}
              register={register}
              name={SMS_THROTTLE_MINUTES.key}
              label={SMS_THROTTLE_MINUTES.label}
              placeholder="60"
              error={
                (errors as Record<string, any>)?.[SMS_THROTTLE_MINUTES.key]
                  ?.message
              }
              hintText="Minimum minutes between repeated ERP connection error SMS. Default 60 (1 hour)."
            />
            <TextInput
              type="number"
              min={1}
              register={register}
              name={SYNC_FAILURE_SMS_THROTTLE_MINUTES.key}
              label={SYNC_FAILURE_SMS_THROTTLE_MINUTES.label}
              placeholder="120"
              error={
                (errors as Record<string, any>)?.[
                  SYNC_FAILURE_SMS_THROTTLE_MINUTES.key
                ]?.message
              }
              hintText="Minimum minutes between SAP sync failure SMS for each type (Order / RMA / Recon). Default 120 (2 hours)."
            />
          </div>
        </FormLayout>

        {/* Third Party FTP Configuration */}
        <h3 className="title mt-3">Third Party FTP Configuration</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <Dropdown
              setValue={setValue}
              defaultValue={
                configuration?.thirdPartyFtp?.protocol ?? FTP_PROTOCOLS.FTP
              }
              options={ftpProtocolOptions}
              name={
                THIRD_PARTY_FTP_PROTOCOL.parentKey +
                '.' +
                THIRD_PARTY_FTP_PROTOCOL.key
              }
              label={THIRD_PARTY_FTP_PROTOCOL.label}
              error={getNestedErrorMessage(errors, THIRD_PARTY_FTP_PROTOCOL)}
            />
            <TextInput
              register={register}
              name={
                THIRD_PARTY_FTP_HOST.parentKey + '.' + THIRD_PARTY_FTP_HOST.key
              }
              label={THIRD_PARTY_FTP_HOST.label}
              error={getNestedErrorMessage(errors, THIRD_PARTY_FTP_HOST)}
            />
            <TextInput
              register={register}
              name={
                THIRD_PARTY_FTP_PORT.parentKey + '.' + THIRD_PARTY_FTP_PORT.key
              }
              label={THIRD_PARTY_FTP_PORT.label}
              error={getNestedErrorMessage(errors, THIRD_PARTY_FTP_PORT)}
            />
            <TextInput
              register={register}
              name={
                THIRD_PARTY_FTP_USERNAME.parentKey +
                '.' +
                THIRD_PARTY_FTP_USERNAME.key
              }
              label={THIRD_PARTY_FTP_USERNAME.label}
              error={getNestedErrorMessage(errors, THIRD_PARTY_FTP_USERNAME)}
            />
            <TextInput
              type="password"
              register={register}
              name={
                THIRD_PARTY_FTP_PASSWORD.parentKey +
                '.' +
                THIRD_PARTY_FTP_PASSWORD.key
              }
              label={THIRD_PARTY_FTP_PASSWORD.label}
              error={getNestedErrorMessage(errors, THIRD_PARTY_FTP_PASSWORD)}
            />
          </div>
        </FormLayout>

        {/* Pharmacy Configuration */}
        <h3 className="title mt-3">Pharmacy Configuration</h3>
        <FormLayout>
          <SwitchField
            control={control}
            name={
              PHARMACY_ENABLE_3PL_IMPORT.parentKey +
              '.' +
              PHARMACY_ENABLE_3PL_IMPORT.key
            }
            label={PHARMACY_ENABLE_3PL_IMPORT.label}
            defaultValue={
              configuration?.pharmacy?.enable3PLProductImport ?? false
            }
          />
        </FormLayout>

        <Button loading={loading} buttonText="Save" className="mt-4 w-32" />
      </form>
    </div>
  )
}

export default ConfigurationForm
